import React, { useState, useEffect } from 'react';
import styled from 'astroturf/react';
import { fetchMiniReport, createReportFromUpload } from './api';
import { MiniReportData, WorkData } from './types';
import WorkCard from './components/WorkCard';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { Spinner } from 'js/design_system/Spinner';
import FileUpload from 'js/design_system/FileUpload';
import { Button } from 'js/design_system/Button';
import { captureException } from '@sentry/browser';
import routes from 'js/core_pre_webpack/routes';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { trackClick } from 'js/utils/trackClick';

interface CiterightMiniReportProps {
  miniReportId: string;
}

export const CiterightMiniReport: React.FC<CiterightMiniReportProps> = ({
  miniReportId,
}) => {
  const [miniReport, setMiniReport] = useState<MiniReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isClaimExpanded, setIsClaimExpanded] = useState(false);

  // Fetch mini report on mount
  useEffect(() => {
    const loadMiniReport = async () => {
      try {
        const response = await fetchMiniReport(miniReportId);
        setMiniReport(response.mini_report);
      } catch (err: unknown) {
        console.error('Failed to fetch mini report:', err);

        if (err instanceof Error && err.message.includes('Unauthorized')) {
          // Redirect to login with post-login redirect back to mini report
          window.location.href = routes.login_path({
            post_login_redirect_url: `/citeright/mini/${miniReportId}`,
          });
          return;
        }

        setError(
          err instanceof Error ? err.message : 'Failed to load mini report',
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadMiniReport();
  }, [miniReportId]);

  // Handle file upload
  const handleUpload = async (acceptedFiles: File[], rejectedFiles: File[]) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a PDF or DOCX file (max 50MB)');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    // Track upload initiation from mini report
    trackClick({
      source: 'citeright.import_click',
      extra: {
        source: 'mini_report',
        mini_report_id: miniReportId,
        claim_category: claim?.category,
        claim_text: claim?.text?.substring(0, 100),
      },
    });

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await createReportFromUpload(file, 'mini_report');

      if (response.report?.report_id) {
        // Redirect to full CiteRight editor
        window.location.href = `/citeright?ui=inline&report_id=${response.report.report_id}`;
      } else {
        setUploadError('Failed to create report. Please try again.');
        setIsUploading(false);
      }
    } catch (err) {
      captureException(err);
      setUploadError('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  // Handle claim toggle
  const handleToggleClaim = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsClaimExpanded(!isClaimExpanded);
  };

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingContainer>
            <Spinner size={48} />
            <Body size="lg">Loading mini report...</Body>
          </LoadingContainer>
        </Container>
      </PageWrapper>
    );
  }

  // Error state
  if (error || !miniReport) {
    return (
      <PageWrapper>
        <Container>
          <ErrorContainer>
            <Icon name="info" size={48} />
            <Heading as="h2">Mini Report Not Available</Heading>
            <Body size="lg" style={{ textAlign: 'center', maxWidth: '500px' }}>
              This mini report is not available.
            </Body>
            <Button onClick={() => (window.location.href = '/citeright')}>
              Create New Report
            </Button>
          </ErrorContainer>
        </Container>
      </PageWrapper>
    );
  }

  const claim = miniReport.claims[0];

  const citedTitles = new Set(miniReport.cited_titles || []);

  // Sort suggested works: non-cited first, then already cited
  const suggestedWorks = [...miniReport.suggested_works].sort((a, b) => {
    const aTitle = a.work?.title?.toLowerCase()?.trim();
    const bTitle = b.work?.title?.toLowerCase()?.trim();

    const aIsCited = aTitle ? citedTitles.has(aTitle) : false;
    const bIsCited = bTitle ? citedTitles.has(bTitle) : false;

    // Non-cited works (false) should come before cited works (true)
    if (aIsCited === bIsCited) return 0;
    return aIsCited ? 1 : -1;
  });

  // Truncation logic
  const truncateLength = 500;
  const shouldTruncate = claim.text.length > truncateLength && !isClaimExpanded;
  const displayText = shouldTruncate
    ? claim.text.slice(0, truncateLength).trim() + '...'
    : claim.text;

  return (
    <>
      <PageWrapper>
        <Container>
          {/* Top Section - Claim Display */}
          <ImpressionWatcher
            source="citeright.mini"
            extra={{
              mini_report_id: miniReportId,
              suggested_works_count: suggestedWorks?.length || 0,
            }}
          >
            <ClaimSection>
              <HighlightedClaim>
                {displayText}
                {claim.text.length > truncateLength && (
                  <>
                    {' '}
                    <ClaimShowMoreLink onClick={handleToggleClaim}>
                      {isClaimExpanded ? 'Show less' : 'Show more'}
                    </ClaimShowMoreLink>
                  </>
                )}
              </HighlightedClaim>
            </ClaimSection>
          </ImpressionWatcher>

          {/* Middle Section - Recommendations */}
          <RecommendationsSection>
            <WorksList>
              <Body size="sm" bold style={{ padding: '0 0 12px 8px' }}>
                References that support this claim
              </Body>
              <div>
                {suggestedWorks.map((workData: WorkData, index: number) => (
                  <WorkCard
                    key={index}
                    workData={{ ...workData, reasoning: undefined }}
                    claimText={claim.text}
                    showAddToListButton={false}
                    showPaperEvaluationChatButton={false}
                    pageContext="mini_report"
                    citedTitles={citedTitles}
                  />
                ))}
              </div>
            </WorksList>
          </RecommendationsSection>
        </Container>
      </PageWrapper>

      {/* Bottom Section - Sticky Upload - Full Width */}
      <StickyUploadSection>
        <UploadInnerContainer>
          {uploadError && (
            <ErrorMessageWrapper>
              <ErrorMessage>
                <Icon name="error" size={20} />
                {uploadError}
              </ErrorMessage>
            </ErrorMessageWrapper>
          )}

          <SubsectionHeading as="h3">
            Get reference suggestions for your draft for free
          </SubsectionHeading>

          <UploadWrapper>
            <FileUpload
              onNewFiles={handleUpload}
              acceptedFileExtensions={['.pdf', '.docx']}
              multiple={false}
              primary={true}
              disabled={isUploading}
              description="your DOCX or PDF"
              note="Maximum file size: 50MB"
              buttonText={isUploading ? 'Uploading...' : 'Choose a draft'}
            />
          </UploadWrapper>
        </UploadInnerContainer>
      </StickyUploadSection>
    </>
  );
};

export default CiterightMiniReport;

const PageWrapper = styled.div`
  background-color: var(--background-beige-light);
  padding: 40px 0 0 0;
  box-sizing: border-box;

  @media (min-width: 769px) {
    min-height: 100vh;
    padding-bottom: 315px; /* Account for fixed footer height (~307px) + 8px buffer */
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const ClaimSection = styled.div`
  margin-bottom: 12px;
`;

const HighlightedClaim = styled.div`
  background-color: #fee940;
  padding: 4px 0px;
  font-size: 16px;
  font-family: 'Georgia', serif;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 16px;
  text-decoration: underline dotted;
  display: inline;
`;

const RecommendationsSection = styled.div`
  margin-bottom: 16px;
`;

const WorksList = styled.div`
  background: white;
  overflow: hidden;
  padding: 16px;
`;

const StickyUploadSection = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: white;
  border-top: 2px solid var(--stroke-light, #dddde2);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  padding: 20px 16px 12px 16px;
  width: 100%;

  @media (max-width: 768px) {
    position: static;
    left: auto;
    right: auto;
    width: auto;
    padding: 16px 0 80px 0;
    margin-top: 24px;
  }
`;

const UploadInnerContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 16px;
  }
`;

const UploadWrapper = styled.div`
  /* Grid column will control width naturally */

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    & > div,
    & > div > div,
    & div[class*='Dropzone'],
    & div[class*='dropzone'] {
      max-width: 100%;
      width: 100%;
      box-sizing: border-box;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 24px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: var(--background-error);
  border: 1px solid var(--stroke-error);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-error);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const ErrorMessageWrapper = styled.div`
  grid-column: 1 / -1;
`;

const SubsectionHeading = styled(Heading)`
  margin-bottom: 0;
`;

const ClaimShowMoreLink = styled.button`
  background: none;
  border: none;
  color: var(--brand-colors-blue-600);
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
  padding: 0;
  margin-left: 4px;
  font-style: italic;

  &:hover {
    color: var(--brand-colors-blue-700);
  }
`;
